import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BatteryCharging,
  CalendarRange,
  Eye,
  Files,
  LucideAngularModule,
  Pencil,
  RefreshCcw,
  Search,
} from 'lucide-angular';
import { catchError, forkJoin, of } from 'rxjs';
import { BatteryConfiguration, BessForecastAsset, BessForecastBlock, BessForecastResponse, BessSchedule } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { BatteryConfigurationsService } from '../services/battery-configurations.service';
import { BessOperationsService } from '../services/bess-operations.service';

type BidMonitorProduct = 'DAM' | 'RTM';
type ExchangeKey = 'IEX' | 'PXIL' | 'HPX';
type SessionOption = { value: string; label: string; blockNumber: number | null };
type BidMonitorRow = {
  id: string;
  serial: number;
  plantName: string;
  balanceRs: number;
  portfolioNo: string;
  exchange: ExchangeKey;
  session: string;
  bidNature: string;
  bidQuantum: number;
  marketPrice: number;
  status: string;
  exchangeStatus: string;
  blockNumber: number | null;
};
type ExchangeSummary = {
  exchange: ExchangeKey;
  submitted: number;
  cancelled: number;
  resubmitted: number;
  approved: number;
  noFunds: number;
  noBid: number;
  pending: number;
  rejected: number;
  failed: number;
  total: number;
};

@Component({
  standalone: true,
  selector: 'app-bess-bid-monitor-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './bess-bid-monitor.page.html',
  styleUrl: './bess-bid-monitor.page.scss',
})
export class BessBidMonitorPage {
  private readonly batteryService = inject(BatteryConfigurationsService);
  private readonly operationsService = inject(BessOperationsService);
  private readonly toast = inject(ToastService);

  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly CalendarRangeIcon = CalendarRange;
  protected readonly SearchIcon = Search;
  protected readonly RefreshCcwIcon = RefreshCcw;
  protected readonly EyeIcon = Eye;
  protected readonly PencilIcon = Pencil;
  protected readonly FilesIcon = Files;

  protected readonly loading = signal(false);
  protected readonly assets = signal<BatteryConfiguration[]>([]);
  protected readonly schedules = signal<BessSchedule[]>([]);
  protected readonly forecasts = signal<BessForecastResponse | null>(null);
  protected readonly product = signal<BidMonitorProduct>('RTM');
  protected readonly selectedDate = signal(this.defaultDate('RTM'));
  protected readonly selectedExchange = signal<ExchangeKey>('IEX');
  protected readonly selectedSession = signal('auto');
  protected readonly plantQuery = signal('');
  protected readonly iexUser = signal('scheduler-rtm');
  protected readonly iexUsers = ['scheduler-rtm', 'ops-desk', 'trader-1'];
  protected readonly exchanges: ExchangeKey[] = ['IEX', 'PXIL', 'HPX'];

  protected readonly pageTitle = computed(() => `${this.product()} Bid Monitor`);
  protected readonly currentBlock = computed(() => this.resolveCurrentBlock());
  protected readonly currentBlockLabel = computed(() => this.blockLabel(this.currentBlock()));
  protected readonly currentBlockDate = computed(() => this.isoDateValue(new Date()));
  protected readonly forecastAssets = computed<BessForecastAsset[]>(() => {
    const response = this.forecasts();
    const product = this.product();
    return response?.products?.[product] || [];
  });
  protected readonly sessionOptions = computed<SessionOption[]>(() => {
    const seen = new Set<number>();
    const options: SessionOption[] = [{ value: 'auto', label: this.product() === 'RTM' ? 'Next live session' : 'Best DAM session', blockNumber: null }];
    for (const asset of this.forecastAssets()) {
      for (const block of asset.blocks || []) {
        if (seen.has(block.block_number)) continue;
        seen.add(block.block_number);
        options.push({
          value: String(block.block_number),
          label: `${block.block_number} - (${block.block_label})`,
          blockNumber: block.block_number,
        });
      }
    }
    return options.sort((left, right) => (left.blockNumber || 0) - (right.blockNumber || 0));
  });
  protected readonly monitorRows = computed<BidMonitorRow[]>(() => {
    const term = this.plantQuery().trim().toLowerCase();
    const exchange = this.selectedExchange();
    return this.assets()
      .map((asset, index) => this.buildRow(asset, index + 1))
      .filter((row): row is BidMonitorRow => !!row)
      .filter((row) => row.exchange === exchange)
      .filter((row) => !term || row.plantName.toLowerCase().includes(term) || row.portfolioNo.toLowerCase().includes(term));
  });
  protected readonly exchangeSummaries = computed<Record<ExchangeKey, ExchangeSummary>>(() => {
    const iexRows = this.assets()
      .map((asset, index) => this.buildRow(asset, index + 1))
      .filter((row): row is BidMonitorRow => !!row);
    return {
      IEX: this.summarizeExchange('IEX', iexRows),
      PXIL: this.summarizeExchange('PXIL', []),
      HPX: this.summarizeExchange('HPX', []),
    };
  });
  protected readonly timeLeft = computed(() => this.buildTimeLeftLabel());

  constructor() {
    this.load();
  }

  protected setProduct(value: string): void {
    const next: BidMonitorProduct = value === 'DAM' ? 'DAM' : 'RTM';
    this.product.set(next);
    this.selectedDate.set(this.defaultDate(next));
    this.selectedSession.set('auto');
    this.load();
  }

  protected setDate(value: string): void {
    this.selectedDate.set(value || this.defaultDate(this.product()));
  }

  protected setExchange(value: string): void {
    const next: ExchangeKey = value === 'PXIL' || value === 'HPX' ? value : 'IEX';
    this.selectedExchange.set(next);
  }

  protected setSession(value: string): void {
    this.selectedSession.set(value || 'auto');
  }

  protected setPlantQuery(value: string): void {
    this.plantQuery.set(value || '');
  }

  protected setIexUser(value: string): void {
    this.iexUser.set(value || '');
  }

  protected applyFilters(): void {
    this.load();
  }

  protected resetFilters(): void {
    const product = this.product();
    this.selectedDate.set(this.defaultDate(product));
    this.selectedExchange.set('IEX');
    this.selectedSession.set('auto');
    this.plantQuery.set('');
    this.iexUser.set(product === 'RTM' ? 'scheduler-rtm' : 'ops-desk');
    this.load();
  }

  protected balanceClass(row: BidMonitorRow): string {
    if (row.balanceRs > 0) return 'balance-positive';
    if (row.balanceRs < 0) return 'balance-negative';
    return 'balance-neutral';
  }

  protected statusClass(value: string): string {
    const normalized = value.toLowerCase();
    if (normalized.includes('submitted') || normalized.includes('approved')) return 'status-good';
    if (normalized.includes('pending')) return 'status-warn';
    if (normalized.includes('failed') || normalized.includes('rejected')) return 'status-bad';
    return 'status-muted';
  }

  protected actionClass(value: string): string {
    const normalized = value.toLowerCase();
    if (normalized === 'sell' || normalized === 'discharge') return 'nature-sell';
    if (normalized === 'buy' || normalized === 'charge') return 'nature-buy';
    return 'nature-hold';
  }

  protected previewRow(row: BidMonitorRow): void {
    this.toast.show(`Plant ${row.plantName} | Session ${row.session}`, '', 'info');
  }

  protected editRow(row: BidMonitorRow): void {
    this.toast.show(`Bid monitor row opened for ${row.plantName}`, '', 'info');
  }

  protected cloneRow(row: BidMonitorRow): void {
    this.toast.show(`Bid setup copied for ${row.plantName}`, '', 'success');
  }

  private load(): void {
    this.loading.set(true);
    let hasFailure = false;
    forkJoin({
      assets: this.batteryService.list().pipe(
        catchError(() => {
          hasFailure = true;
          return of<BatteryConfiguration[]>([]);
        }),
      ),
      schedules: this.operationsService.listSchedules().pipe(
        catchError(() => {
          hasFailure = true;
          return of<BessSchedule[]>([]);
        }),
      ),
      forecasts: this.operationsService.listForecasts(this.product(), this.selectedDate()).pipe(
        catchError(() => {
          hasFailure = true;
          return of<BessForecastResponse | null>(null);
        }),
      ),
    }).subscribe({
      next: ({ assets, schedules, forecasts }) => {
        this.assets.set(assets || []);
        this.schedules.set(schedules || []);
        this.forecasts.set(forecasts);
        this.loading.set(false);
        if (hasFailure) {
          this.toast.show('Bid monitor loaded with partial data', '', 'info');
        }
      },
      error: () => {
        this.loading.set(false);
        this.toast.show('Failed to load BESS bid monitor', '', 'danger');
      },
    });
  }

  private buildRow(asset: BatteryConfiguration, serial: number): BidMonitorRow | null {
    const forecastAsset =
      this.forecastAssets().find((item) => item.battery_configuration_id === asset.id) ||
      this.forecastAssets().find((item) => item.entry_id === asset.entry_id) ||
      null;
    const selectedBlock = this.resolveSelectedBlock(forecastAsset);
    const schedule = this.findSchedule(asset.id, selectedBlock?.block_number || null);
    const action = this.resolveBidNature(schedule?.action, selectedBlock?.action);
    const marketPrice = this.toNumber(schedule?.market_price ?? selectedBlock?.market_price);
    const bidQuantum = Math.abs(this.toNumber(schedule?.energy_mw ?? selectedBlock?.forecast_mw ?? asset.power_capacity_mw));
    const status = this.resolveStatus(schedule, action);
    const exchangeStatus = this.resolveExchangeStatus(status);
    const sign = action === 'SELL' ? 1 : action === 'BUY' ? -1 : 0;
    const balanceRs = sign * bidQuantum * (marketPrice || 0) * 100;

    return {
      id: asset.id,
      serial,
      plantName: asset.entry_id || `Plant ${serial}`,
      balanceRs,
      portfolioNo: this.buildPortfolioNo(asset),
      exchange: 'IEX',
      session: selectedBlock?.block_label || (this.product() === 'DAM' ? 'All Day' : this.currentBlockLabel()),
      bidNature: action,
      bidQuantum,
      marketPrice,
      status,
      exchangeStatus,
      blockNumber: selectedBlock?.block_number || null,
    };
  }

  private summarizeExchange(exchange: ExchangeKey, rows: BidMonitorRow[]): ExchangeSummary {
    const exchangeRows = rows.filter((row) => row.exchange === exchange);
    const count = (matcher: (row: BidMonitorRow) => boolean) => exchangeRows.filter(matcher).length;
    return {
      exchange,
      submitted: count((row) => row.status.toLowerCase() === 'submitted'),
      cancelled: count((row) => row.status.toLowerCase() === 'cancelled/deleted'),
      resubmitted: count((row) => row.status.toLowerCase() === 're-submitted'),
      approved: count((row) => row.exchangeStatus.toLowerCase() === 'approved'),
      noFunds: count((row) => row.status.toLowerCase() === 'no funds'),
      noBid: count((row) => row.status.toLowerCase() === 'no bid'),
      pending: count((row) => row.status.toLowerCase() === 'pending'),
      rejected: count((row) => row.status.toLowerCase() === 'rejected'),
      failed: count((row) => row.status.toLowerCase() === 'failed'),
      total: exchangeRows.length,
    };
  }

  private resolveSelectedBlock(asset: BessForecastAsset | null): BessForecastBlock | null {
    const blocks = asset?.blocks || [];
    if (!blocks.length) {
      return null;
    }
    const session = this.selectedSession();
    if (session !== 'auto') {
      const target = Number(session);
      return blocks.find((item) => item.block_number === target) || blocks[0];
    }
    const currentBlock = this.currentBlock();
    const preferred = this.product() === 'RTM'
      ? blocks.find((item) => item.block_number >= Math.max(currentBlock + 1, this.forecasts()?.forecast_start_block || 1))
      : blocks.find((item) => item.action?.toLowerCase() !== 'hold');
    return preferred || blocks[0];
  }

  private findSchedule(assetId: string, blockNumber: number | null): BessSchedule | null {
    if (!blockNumber) {
      return null;
    }
    return (
      this.schedules().find(
        (item) => item.battery_configuration_id === assetId && item.schedule_date === this.selectedDate() && item.time_block === blockNumber,
      ) || null
    );
  }

  private resolveBidNature(scheduleAction?: string | null, forecastAction?: string | null): string {
    const normalized = (scheduleAction || forecastAction || '').toLowerCase();
    if (normalized === 'discharge' || normalized === 'sell') return 'SELL';
    if (normalized === 'charge' || normalized === 'buy') return 'BUY';
    return 'HOLD';
  }

  private resolveStatus(schedule: BessSchedule | null, action: string): string {
    const status = (schedule?.status || '').toLowerCase();
    if (status === 'submitted') return 'Submitted';
    if (status === 'approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    if (status === 'failed') return 'Failed';
    if (status === 'cancelled' || status === 'deleted') return 'Cancelled/Deleted';
    if (status === 'resubmitted') return 'Re-Submitted';
    if (status === 'no funds') return 'No Funds';
    if (status === 'pending') return 'Pending';
    return action === 'HOLD' ? 'No Bid' : 'Pending';
  }

  private resolveExchangeStatus(status: string): string {
    const normalized = status.toLowerCase();
    if (normalized === 'submitted' || normalized === 'approved') return 'Approved';
    if (normalized === 'pending') return 'Pending';
    if (normalized === 'failed') return 'Failed';
    if (normalized === 'rejected') return 'Rejected';
    if (normalized === 'cancelled/deleted') return 'Cancelled';
    return '-';
  }

  private buildPortfolioNo(asset: BatteryConfiguration): string {
    const source = (asset.entry_id || asset.id).replace(/[^a-z0-9]/gi, '').toUpperCase();
    return `B${source.slice(0, 11).padEnd(11, '0')}`;
  }

  private buildTimeLeftLabel(): string {
    if (this.product() !== 'RTM' || this.selectedDate() !== this.currentBlockDate()) {
      return 'Not live';
    }
    const option = this.selectedSession() === 'auto'
      ? this.sessionOptions().find((item) => item.value !== 'auto')
      : this.sessionOptions().find((item) => item.value === this.selectedSession());
    const targetBlock = option?.blockNumber;
    if (!targetBlock) {
      return 'Awaiting session';
    }
    const targetStart = this.blockStartDate(this.selectedDate(), targetBlock);
    const diffMs = targetStart.getTime() - Date.now();
    if (diffMs <= 0) {
      return `${targetBlock} - live`;
    }
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = `${Math.floor(totalSeconds / 3600)}`.padStart(2, '0');
    const minutes = `${Math.floor((totalSeconds % 3600) / 60)}`.padStart(2, '0');
    const seconds = `${totalSeconds % 60}`.padStart(2, '0');
    return `${targetBlock} - (${hours}:${minutes}:${seconds})`;
  }

  private blockStartDate(dateValue: string, blockNumber: number): Date {
    const [year, month, day] = dateValue.split('-').map((item) => Number(item));
    const start = new Date(year, (month || 1) - 1, day || 1, 0, 0, 0, 0);
    start.setMinutes((Math.max(1, blockNumber) - 1) * 15);
    return start;
  }

  private resolveCurrentBlock(): number {
    const now = new Date();
    return Math.min(96, Math.max(1, Math.floor((now.getHours() * 60 + now.getMinutes()) / 15) + 1));
  }

  private blockLabel(blockNumber: number): string {
    const start = this.blockStartDate(this.currentBlockDate(), blockNumber);
    const end = new Date(start.getTime() + 15 * 60 * 1000);
    return `${this.timeLabel(start)}-${this.timeLabel(end)}`;
  }

  private timeLabel(value: Date): string {
    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private defaultDate(product: BidMonitorProduct): string {
    const now = new Date();
    if (product === 'DAM') {
      now.setDate(now.getDate() + 1);
    }
    return this.isoDateValue(now);
  }

  private isoDateValue(value: Date): string {
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  private toNumber(value: string | number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
